const FRONT_COVER_SRC = "/assets/facility-report/facility_report_front.png";
const BACK_COVER_SRC = "/assets/facility-report/facilty_report_back.png";

// Banner proportions matched to the PDF cover's design (24pt font / 16pt
// vertical / 36pt horizontal padding against a 767.25pt-tall reference page,
// converted to px at 1px = 0.75pt).
const FONT_SIZE = 32;
const PADDING_VERTICAL = 21.33;
const PADDING_HORIZONTAL = 48;
const ROTATION_DEG = -12;
const BAND_TOP_FRACTION = 0.63;
const MAX_WIDTH_FRACTION = 0.85;

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });

const dataUrlToBase64 = (dataUrl: string) => dataUrl.split(",")[1];

export const createFrontCoverImage = async (
  label: string
): Promise<string> => {
  const img = await loadImage(FRONT_COVER_SRC);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  ctx.drawImage(img, 0, 0);

  const fontFaceSet = (document as { fonts?: { load: (font: string) => Promise<unknown> } })
    .fonts;
  if (fontFaceSet?.load) {
    await fontFaceSet.load(`700 ${FONT_SIZE}px "Noto Sans JP"`);
  }

  const centerX = canvas.width / 2;
  const bandTop = canvas.height * BAND_TOP_FRACTION;
  const maxWidth = canvas.width * MAX_WIDTH_FRACTION;
  const maxTextWidth = maxWidth - PADDING_HORIZONTAL * 2;

  ctx.font = `700 ${FONT_SIZE}px "Noto Sans JP", sans-serif`;
  let textWidth = ctx.measureText(label).width;
  let fontSize = FONT_SIZE;
  if (textWidth > maxTextWidth) {
    fontSize = FONT_SIZE * (maxTextWidth / textWidth);
    ctx.font = `700 ${fontSize}px "Noto Sans JP", sans-serif`;
    textWidth = maxTextWidth;
  }

  const boxWidth = Math.min(
    textWidth + PADDING_HORIZONTAL * 2,
    maxWidth
  );
  const boxHeight = FONT_SIZE * 1.2 + PADDING_VERTICAL * 2;
  const boxX = centerX - boxWidth / 2;
  const boxY = bandTop;
  const pivotX = centerX;
  const pivotY = bandTop + boxHeight / 2;

  ctx.save();
  ctx.translate(pivotX, pivotY);
  ctx.rotate((ROTATION_DEG * Math.PI) / 180);
  ctx.translate(-pivotX, -pivotY);

  ctx.fillStyle = "#000000";
  ctx.fillRect(boxX, boxY, boxWidth, boxHeight);

  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, centerX, boxY + boxHeight / 2);
  ctx.restore();

  return dataUrlToBase64(canvas.toDataURL("image/png"));
};

export const createBackCoverImage = async (): Promise<string> => {
  const response = await fetch(BACK_COVER_SRC);
  const buffer = await response.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};
